import * as tslib_1 from "tslib";
import { ErrorHandler } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { Metar } from '../models/metar.model';
import { Taf } from '../models/taf.model';
/**
 * Default radius (in nautical miles) to use when querying for METARs by lat/lon
 * @type {number}
 */
var DEFAULT_METAR_RADIUS = 25;
/**
 * Default radius (in nautical miles) to use when querying for TAFs by lat/lon
 * @type {number}
 */
var DEFAULT_TAF_RADIUS = 50;
/**
 * Service for querying weather information (METARs and TAFs)
 */
var WeatherService = /** @class */ (function () {
    function WeatherService(_http, _errorHandler) {
        this._http = _http;
        this._errorHandler = _errorHandler;
    }
    /**
     * Returns the latest METAR for a given station identifier
     * @param {string} identifier Station identifier
     * @returns {Promise<Metar>} METAR
     */
    WeatherService.prototype.getMetar = function (identifier) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, data, e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/wx/metars?query=" + identifier).toPromise()];
                    case 1:
                        response = _a.sent();
                        data = response.find(function (d) { return d.icao_code === identifier; });
                        return [2 /*return*/, data ? Metar.parse(data) : null];
                    case 2:
                        e_1 = _a.sent();
                        if (e_1 && e_1.status === 404) {
                            return [2 /*return*/, null];
                        }
                        this._errorHandler.handleError(e_1);
                        throw e_1;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Queries for METARs within a given distance of a location
     * @param {LatLon} latlon Location
     * @param {number} [radius] Radius distance (in nautical miles)
     * @param {number} [maximum] Maximum number of results to query. If not specified, all results within the given
     *  distance will be returned.
     * @returns {Promise<Array<Metar>>} Latest METARs within the specified area
     */
    WeatherService.prototype.getMetarsByLatLon = function (latlon, radius, maximum) {
        if (radius === void 0) { radius = DEFAULT_METAR_RADIUS; }
        if (maximum === void 0) { maximum = null; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var body, url, response, e_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        body = {
                            latitude: latlon.lat,
                            longitude: latlon.lon,
                            radius: radius
                        };
                        if (maximum !== null) {
                            body.maximum = maximum;
                        }
                        url = environment.airportsApiUrl + "/wx/metars?latitude=" + latlon.lat + "&longitude=" + latlon.lon + "&radius=" + radius;
                        return [4 /*yield*/, this._http.get(url).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.map(Metar.parse)];
                    case 2:
                        e_2 = _a.sent();
                        if (e_2 && e_2.status === 404) {
                            return [2 /*return*/, null];
                        }
                        this._errorHandler.handleError(e_2);
                        throw e_2;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns the latest TAF for a given station identifier
     * @param {string} identifier Station identifier
     * @returns {Promise<Taf>} TAF
     */
    WeatherService.prototype.getTaf = function (identifier) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, data, e_3;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/wx/tafs?query=" + identifier).toPromise()];
                    case 1:
                        response = _a.sent();
                        data = response.find(function (d) { return d.icao_code === identifier; });
                        return [2 /*return*/, data ? Taf.parse(data) : null];
                    case 2:
                        e_3 = _a.sent();
                        if (e_3 && e_3.status === 404) {
                            return [2 /*return*/, null];
                        }
                        this._errorHandler.handleError(e_3);
                        throw e_3;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Queries for TAFs within a given distance of a location
     * @param {LatLon} latlon Location
     * @param {number} [radius] Radius distance (in nautical miles)
     * @param {number} [maximum] Maximum number of results to query. If not specified, all results within the given
     *  distance will be returned.
     * @returns {Promise<Array<Taf>>} Latest TAFs within the specified area
     */
    WeatherService.prototype.getTafsByLatLon = function (latlon, radius, maximum) {
        if (radius === void 0) { radius = DEFAULT_TAF_RADIUS; }
        if (maximum === void 0) { maximum = null; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var body, url, response, e_4;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        body = {
                            latitude: latlon.lat,
                            longitude: latlon.lon,
                            radius: radius
                        };
                        if (maximum !== null) {
                            body.maximum = maximum;
                        }
                        url = environment.airportsApiUrl + "/wx/tafs?latitude=" + latlon.lat + "&longitude=" + latlon.lon + "&radius=" + radius;
                        return [4 /*yield*/, this._http.get(url).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.map(Taf.parse)];
                    case 2:
                        e_4 = _a.sent();
                        if (e_4 && e_4.status === 404) {
                            return [2 /*return*/, null];
                        }
                        this._errorHandler.handleError(e_4);
                        throw e_4;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    return WeatherService;
}());
export { WeatherService };
